import axios from 'axios';

// In development: set REACT_APP_API_URL=http://localhost:5000/api in client/.env
// In production (Railway): leave REACT_APP_API_URL unset — falls back to relative '/api'
const API_URL = process.env.REACT_APP_API_URL || '/api';

// Base origin for media files (images, avatars) — strips '/api' suffix
const MEDIA_BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api$/, '')
  : '';

// Returns a fully-qualified URL for uploaded media paths (e.g. '/uploads/images/foo.jpg')
export const getMediaUrl = (path) => (path ? `${MEDIA_BASE}${path}` : null);

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('am_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('am_token');
      localStorage.removeItem('am_user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Users
export const userAPI = {
  getProfile: (id) => api.get(`/users/profile/${id}`),
  updateProfile: (data) => api.put('/users/profile', data),
  uploadAvatar: (formData) => api.post('/users/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  becomeSeller: (data) => api.put('/users/become-seller', data),
};

// Categories
export const categoryAPI = {
  getAll: () => api.get('/categories'),
  getBySlug: (slug) => api.get(`/categories/${slug}`),
};

// Services
export const serviceAPI = {
  search: (params) => api.get('/services', { params }),
  getById: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  uploadImages: (id, formData) => api.post(`/services/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Orders
export const orderAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
};

// Reviews
export const reviewAPI = {
  getByService: (serviceId, params) => api.get(`/reviews/service/${serviceId}`, { params }),
  create: (data) => api.post('/reviews', data),
};

// Payments
export const paymentAPI = {
  createCheckout: (data) => api.post('/payments/create-checkout', data),
  createSellerAccount: () => api.post('/payments/create-seller-account'),
  getSellerBalance: () => api.get('/payments/seller-balance'),
  subscribe: (tier) => api.post('/payments/subscribe', { tier }),
};

// Messages
export const messageAPI = {
  getConversations: () => api.get('/messages/conversations'),
  getConversation: (id, params) => api.get(`/messages/conversations/${id}`, { params }),
  send: (data) => api.post('/messages', data),
};

// Admin
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  getOrders: (params) => api.get('/admin/orders', { params }),
  toggleFeatured: (id) => api.put(`/admin/services/${id}/feature`),
};

export default api;
